import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { HierarchyView } from 'sharedComponents/HierarchyView';
import {
    transformHierarchyToTree,
    generateAllNodeIds,
    renderIcon,
    getDownstreamHierarchy,
} from '../../PowertrainTools/PowertrainConfigurationV2/Hierarchy/helpers';
import { NODE_TYPE } from '../../PowertrainTools/PowertrainConfigurationV2/Hierarchy/constants';
// import { powertrainV2API } from 'api/PowertrainV2API';PowertrainApiV2
import { PowertrainApiV2 } from 'api';
import './style.scss';
import { filterTreeByTypes } from 'sharedComponents/HierarchyView/helpers';

const LocationHierarchyDropdown = ({
    hierarchyData = null,
    selectedSite = null,
    selectedLocation = null,
    organizationId = null,
    onSelect = () => { },
    placeholder = 'Select',
    disabled = false,
    isForEditHierarchy = false,
    parentNodeId = null,
    selectedNodeId = null
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [fetchedSiteHierarchyData, setFetchedSiteHierarchyData] = useState(null);
    const containerRef = useRef(null);
    const controlRef = useRef(null);

    const ROW_HEIGHT = 45;
    const MAX_DROPDOWN_HEIGHT = 300;

    // Fetch hierarchy data when selected site changes
    // This also triggers on initial mount if selectedSite is already set
    useEffect(() => {
        if (!selectedSite?.siteId || !organizationId || isForEditHierarchy) {
            setFetchedSiteHierarchyData(null);
            return;
        }

        const fetchSiteHierarchy = async () => {
            setIsLoading(true);
            try {
                const response = await PowertrainApiV2.getHierarchyDataForSite(organizationId, [selectedSite.siteId]);
                setFetchedSiteHierarchyData(response?.data || response);
            } catch (error) {
                setFetchedSiteHierarchyData(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchSiteHierarchy();
    }, [selectedSite?.siteId, organizationId, isForEditHierarchy]);

    // Transform full hierarchy into tree nodes (no powertrains)
    const transformedTreeData = useMemo(() => {
        // Use fetched site hierarchy if available, otherwise use full hierarchy
        const dataToTransform = fetchedSiteHierarchyData || hierarchyData;
        if (!dataToTransform) { return []; }
        const data = Array.isArray(dataToTransform) ? dataToTransform : [dataToTransform];
        return transformHierarchyToTree(data, true, false);
    }, [fetchedSiteHierarchyData, hierarchyData]);

    const treeData = isForEditHierarchy ? hierarchyData : transformedTreeData;

    // When a site is selected, show only that site's subtree (nested locations).
    // When no site is selected, show the full hierarchy.
    const displayTreeData = useMemo(() => {
        let data = treeData;

        if (selectedSite?.siteId) {
            const subtree = getDownstreamHierarchy(treeData, selectedSite.siteId, NODE_TYPE.SITE);
            data = subtree.length > 0 ? subtree : treeData;
        }

        // Filter to show only Location and Site node types
        const hiddenNodeTypes = [
            NODE_TYPE.ORGANIZATION,
            NODE_TYPE.SUB_ORGANIZATION,
            NODE_TYPE.POWERTRAIN,
            NODE_TYPE.UNASSIGNED_POWERTRAINS,
            NODE_TYPE.UNASSIGNED_POWERTRAIN,
            NODE_TYPE.UNASSIGNED_ASSETS
        ];

        return filterTreeByTypes(data, hiddenNodeTypes);
    }, [treeData, selectedSite]);

    // All node IDs — passed as initial expandedIds so the tree starts fully expanded.
    // HierarchyView (isflattenData=true) manages its own localExpandedIds from this.
    const allNodeIds = useMemo(() => generateAllNodeIds(displayTreeData), [displayTreeData]);

    // Validate selected location exists in fetched hierarchy data
    const isSelectedLocationValid = useMemo(() => {
        if (!selectedLocation?.locationId || !fetchedSiteHierarchyData) {
            return true; // No validation needed if no fetched data or no selected location
        }
        // Check if location exists in fetched data
        const locationExists = allNodeIds.includes(selectedLocation.locationId);
        return locationExists;
    }, [selectedLocation?.locationId, fetchedSiteHierarchyData, allNodeIds]);

    // All nodes are selectable except SITE nodes
    const isNodeSelectable = useCallback((node) => {
        if (isForEditHierarchy) {
            return node?.id !== parentNodeId && node?.id !== selectedNodeId;
        }
        return node?.data?.type !== NODE_TYPE.SITE;
    }, [isForEditHierarchy, parentNodeId]);

    // Helper to find a node by ID in the tree
    const findNodeById = useCallback((nodeId, nodes = displayTreeData) => {
        if (!nodes || !Array.isArray(nodes)) { return null; }

        for (const node of nodes) {
            if (node.id === nodeId) {
                return node;
            }
            if (node.children && node.children.length > 0) {
                const found = findNodeById(nodeId, node.children);
                if (found) { return found; }
            }
        }
        return null;
    }, [displayTreeData]);

    const handleSelect = useCallback((selectedIds) => {

        // HierarchyView passes an array of IDs, not full nodes
        // Get the first selected ID
        const selectedId = selectedIds?.[0];
        if (!selectedId) {
            return;
        }

        // Look up the full node from displayTreeData
        const node = findNodeById(selectedId);

        // The node.data already contains ALL location properties (spread by transformHierarchyToTree)
        // Extract all properties from the node
        const enrichedLocation = {
            locationId: node.data?.locationId,
            locationName: node.name,  // Get name from tree node
            name: node.name,
            siteId: node.data?.siteId,
            id: node.id,
            // Include ALL other properties from node.data
            ...node.data
        };

        // Pass complete location object to parent
        onSelect(enrichedLocation);
        setIsOpen(false);
    }, [onSelect, findNodeById]);

    // Clear the current location selection without opening the dropdown
    const handleClear = useCallback((e) => {
        e.stopPropagation();
        onSelect(null);
        setIsOpen(false);
    }, [onSelect]);

    // Count all nodes recursively (all start expanded, so all are visible)
    const countAllNodes = useCallback((nodes) => {
        if (!nodes || !Array.isArray(nodes)) { return 0; }
        return nodes.reduce((count, node) => count + 1 + countAllNodes(node.children), 0);
    }, []);

    const dropdownHeight = useMemo(() => {
        if (!isOpen) { return MAX_DROPDOWN_HEIGHT; }
        const totalNodes = countAllNodes(displayTreeData);
        // Give HierarchyView the full content height so it renders ALL nodes.
        // The scrollable wrapper div (CSS max-height) handles capping and the scrollbar.
        return Math.max(totalNodes * ROW_HEIGHT, ROW_HEIGHT);
    }, [isOpen, displayTreeData, countAllNodes]);

    // Close dropdown on outside click
    useEffect(() => {
        if (!isOpen) { return; }
        const handleOutsideClick = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isOpen]);

    const selectedIds = useMemo(() => {
        // Show selected location only if it's valid in the fetched data
        if (!selectedLocation?.locationId || !isSelectedLocationValid) {
            return [];
        }
        return [selectedLocation.locationId];
    }, [selectedLocation?.locationId, isSelectedLocationValid]);

    const displayLabel = useMemo(() => {
        const label = isSelectedLocationValid
            ? selectedLocation?.locationName || selectedLocation?.name || null
            : null;

        return label;
    }, [selectedLocation?.locationName, selectedLocation?.name, isSelectedLocationValid]);

    return (
        <div
            ref={containerRef}
            className={`location-hierarchy-dropdown${disabled ? ' disabled' : ''}${isLoading ? ' loading' : ''}${isForEditHierarchy ? ' edit-mode' : ''}`}
        >
            {/* Trigger — styled to match GeneralSelect exactly */}
            <div
                ref={controlRef}
                className={`location-hierarchy-control${disabled ? ' disabled' : ''}${isOpen ? ' open' : ''}`}
                onClick={() => !disabled && !isLoading && setIsOpen(prev => !prev)}
                role='combobox'
                aria-expanded={isOpen}
                aria-haspopup='listbox'
                tabIndex={disabled ? -1 : 0}
                onKeyDown={(e) => {
                    if (!disabled && !isLoading && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        setIsOpen(prev => !prev);
                    }
                    if (e.key === 'Escape') { setIsOpen(false); }
                }}
            >
                <span className={`location-hierarchy-value${!displayLabel ? ' placeholder' : ''}`}>
                    {isLoading ? 'Loading...' : displayLabel || placeholder}
                </span>
                {displayLabel && !disabled && !isLoading && !isForEditHierarchy &&
                    <button
                        type='button'
                        className='location-hierarchy-clear'
                        onClick={handleClear}
                        onKeyDown={(e) => e.stopPropagation()}
                        aria-label='Clear location selection'
                        title='Clear selection'
                    >
                        <svg
                            width='16'
                            height='16'
                            viewBox='0 0 24 24'
                            fill='none'
                            strokeWidth='2'
                        >
                            <line x1='18' y1='6' x2='6' y2='18' />
                            <line x1='6' y1='6' x2='18' y2='18' />
                        </svg>
                    </button>
                }
                {!disabled && <span className='location-hierarchy-arrow'>
                    {isLoading ?
                        <svg
                            width='16'
                            height='16'
                            viewBox='0 0 24 24'
                            fill='none'
                            className='loading-spinner'
                        >
                            <circle cx='12' cy='12' r='8' stroke='#a0a0a0' strokeWidth='2' fill='none' />
                        </svg>
                        :
                        <svg
                            width='16'
                            height='16'
                            viewBox='0 0 24 24'
                            fill='none'
                            strokeWidth='2'
                        >
                            <polyline points='6 9 12 15 18 9' />
                        </svg>
                    }
                </span>}
            </div>

            {/* Dropdown panel with HierarchyView */}
            {isOpen && !isLoading &&
                <div className='location-hierarchy-menu' role='listbox'>
                    <div className='location-hierarchy-scroll'>
                        <HierarchyView
                            nodes={displayTreeData}
                            isflattenData={true}
                            selectedIds={selectedIds}
                            expandedIds={allNodeIds}
                            renderIcon={renderIcon}
                            onSelect={handleSelect}
                            dragEnabled={false}
                            dropEnabled={false}
                            searchEnabled={false}
                            editMode={false}
                            isNodeSelectable={isNodeSelectable}
                            height={dropdownHeight}
                            indent={16}
                        />
                    </div>
                </div>
            }
        </div>
    );
};

LocationHierarchyDropdown.propTypes = {
    hierarchyData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    selectedSite: PropTypes.shape({
        siteId: PropTypes.number,
        siteName: PropTypes.string
    }),
    selectedLocation: PropTypes.shape({
        locationId: PropTypes.number,
        locationName: PropTypes.string
    }),
    organizationId: PropTypes.number,
    onSelect: PropTypes.func,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    isForEditHierarchy: PropTypes.bool,
    parentNodeId: PropTypes.number
};

export default LocationHierarchyDropdown;
